import { useState, useEffect } from 'react';
import { Client } from '../../../context/ClientContext';
import { supabase } from '../../../lib/supabase';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Dialog } from '@headlessui/react';
import { Button, Dropdown, SidePanel } from '../../ui';

interface SupabaseUserRow {
  id: string;
  name: string;
  email: string;
}

// Helper function to safely execute Supabase queries with retry logic
const safeSupabaseQuery = async <T,>(queryFn: () => PromiseLike<T>, retries = 3, delay = 1000): Promise<T | undefined> => {
  let attempt = 0;
  while (attempt < retries) {
    try {
      return await queryFn();
    } catch (error) {
      console.error(`Supabase query attempt ${attempt + 1} failed:`, error);
      attempt++;
      if (attempt >= retries) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      // Increase delay for next retry (exponential backoff)
      delay *= 2;
    }
  }
};

interface CreateTaskSidepanelProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  onTaskAdded?: () => void;
}

export default function CreateTaskSidepanel({ isOpen, onClose, client, onTaskAdded }: CreateTaskSidepanelProps) {
  const DRAFT_KEY = `task-draft-${client.id}`;

  // Form states
  const [customTask, setCustomTask] = useState({
    title: '',
    description: '',
    videoUrl: '',
  });
  const [dueDate, setDueDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);
  const [users, setUsers] = useState<SupabaseUserRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showUnassignedModal, setShowUnassignedModal] = useState(false);

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        setCustomTask(draft.customTask || { title: '', description: '', videoUrl: '' });
        setDueDate(draft.dueDate || format(new Date(), 'yyyy-MM-dd'));
        setSelectedAssignee(draft.selectedAssignee || null);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  }, [DRAFT_KEY]);

  // Save draft to localStorage whenever form data changes
  useEffect(() => {
    if (isOpen) {
      try {
        const draft = {
          customTask,
          dueDate,
          selectedAssignee,
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch (error) {
        console.error('Error saving draft:', error);
      }
    }
  }, [customTask, dueDate, selectedAssignee, isOpen, DRAFT_KEY]);

  // Clear draft helper function
  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  };

  // Fetch users for assignment dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const result = await safeSupabaseQuery(() =>
          supabase.from('users').select('id, name, email').order('name')
        );

        if (result?.error) {
          console.error('Error fetching users:', result.error);
          return;
        }

        setUsers((result?.data as SupabaseUserRow[]) || []);
      } catch (error) {
        console.error('Error in fetchUsers:', error);
      }
    };

    fetchUsers();
  }, []);

  // Handle task form submission — checks for unassigned before proceeding
  const handleSubmitTask = () => {
    if (!customTask.title.trim()) {
      setSubmitError('Title is required');
      return;
    }
    if (!selectedAssignee) {
      setShowUnassignedModal(true);
      return;
    }
    performSubmit();
  };

  // Actual submission logic
  const performSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (!customTask.title.trim()) {
        setSubmitError('Title is required');
        setIsSubmitting(false);
        return;
      }

      // Get current user
      const sessionResult = await safeSupabaseQuery(() => supabase.auth.getSession());
      const session = sessionResult?.data?.session;

      if (!session?.user) {
        setSubmitError('No authenticated user found');
        setIsSubmitting(false);
        return;
      }

      // Create the client task
      const insertResult = await safeSupabaseQuery(() =>
        supabase
          .from('client_tasks')
          .insert({
            client_id: client.id,
            title: customTask.title,
            description: customTask.description,
            video_url: customTask.videoUrl,
            status: 'Scheduled',
            due_date: dueDate,
            assigned_by: session.user.id,
            assigned_to: selectedAssignee,
          })
          .select()
      );

      if (insertResult?.error) {
        throw insertResult.error;
      }

      // Reset form
      setCustomTask({
        title: '',
        description: '',
        videoUrl: '',
      });
      setDueDate(format(new Date(), 'yyyy-MM-dd'));
      setSelectedAssignee(null);

      // Clear saved draft
      clearDraft();

      // Close modal and panel
      setShowUnassignedModal(false);
      onClose();
      if (onTaskAdded) onTaskAdded();
    } catch (error) {
      console.error('Error adding task:', error);
      setSubmitError('An error occurred while adding the task.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <SidePanel
      open={isOpen}
      onClose={onClose}
      width={480}
      eyebrow={`Task · ${client.name}`}
      title="Add Task"
      footer={
        <>
          <Button icon="check" onClick={handleSubmitTask} disabled={isSubmitting || !customTask.title.trim()}>
            {isSubmitting ? 'Creating...' : 'Add Task'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="ck-stagger">
        {submitError && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {submitError}
          </div>
        )}

        {/* seamless title */}
        <input
          type="text"
          value={customTask.title}
          onChange={(e) => setCustomTask({ ...customTask, title: e.target.value })}
          placeholder="What are we workin' on?"
          aria-label="Task title"
          className="ck-title-input"
        />

        {/* description */}
        <div style={{ margin: '18px 0 20px' }}>
          <div className="t-eyebrow" style={{ marginBottom: '8px' }}>Description</div>
          <textarea
            value={customTask.description}
            onChange={(e) => setCustomTask({ ...customTask, description: e.target.value })}
            rows={3}
            placeholder="Add a description..."
            className="ck-textarea"
          />
        </div>

        {/* meta grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Due</div>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="ck-date-input"
            />
          </div>
          <Dropdown
            label="Assignee"
            options={[{ value: '', label: 'Unassigned' }, ...users.map((u) => ({ value: u.id, label: u.name }))]}
            value={selectedAssignee || ''}
            onChange={(e) => setSelectedAssignee(e.target.value || null)}
            placeholder="Select assignee..."
          />
        </div>
      </div>
    </SidePanel>

      <Dialog open={showUnassignedModal} onClose={() => setShowUnassignedModal(false)} className="relative z-50">
        <div className="fixed inset-0" style={{ background: 'var(--surface-overlay)' }} aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4 ck-safe-pad">
          <Dialog.Panel
            className="mx-auto max-w-md w-full"
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-pop)',
              padding: 'var(--space-6)',
            }}
          >
            <Dialog.Title
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '11px',
                margin: '0 0 12px',
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                letterSpacing: 'var(--tracking-tight)',
                color: 'var(--text-body)',
              }}
            >
              <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: 'var(--status-warn, #f59e0b)', fontSize: '15px' }} />
              No Assignee?
            </Dialog.Title>
            <p style={{ margin: '0 0 22px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              This task won't show up on anyone's board until it's assigned. Save anyway?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button variant="ghost" onClick={() => setShowUnassignedModal(false)}>Cancel</Button>
              <Button onClick={performSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Save Unassigned'}
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
